import {BookingModel} from "../models/booking.model";

import {IBookingStrategy, ShoppingCartModeEnum} from "./booking-strategy.interface";
import {computed, IReactionDisposer, makeObservable, observable, runInAction} from "mobx";
import {ISsrType} from "../../ssr-types/ssr-types.service.interface";
import {AvailabilityModel} from "../../flight-search/models/availability.model";
import {IServiceFactory} from "../../service-factory.interface";
import {IDotRezBookingSession} from "../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {IBookingStrategyAnalyticsConfiguration} from "./analytics/booking-strategy-analytics-configuration.interface";
import {JourneyModel} from "../models/journey/journey.model";
import {IBookingStrategyToBookingAdapter} from "./booking-strategy-to-booking.adapter.interface";
import {BookingFlowTypeEnum} from "./booking-flow-type.enum";
import {IFareToSell} from "../models/booking-view-model.interface";
import {
    IDotRezBookingSessionData,
    IDotRezPartialBookingSessionData
} from "../../dot-rez-api/data-contracts/booking/dot-rez-booking-session-data.interface";
import {BookingSnapshotModel} from "../models/snapshots/booking/booking-snapshot.model";
import {IRoundTripLowFareReader} from "../../low-fare/low-fare-readers/low-fare-reader.interface";
import {RoundTripLowFareReaderFactory} from "../../low-fare/round-trip-low-fare-reader-factory";
import {IFlightSearchControllerViewModel} from "../../flight-search/flight-search-controller/flight-search-controller-view-model.interface";
import {IAvailabilityFareReducer} from "../../flight-search/models/fare-reducer.interface";
import {Price} from "../../currency/price";
import {createEmptyBookingSessionData} from "../models/create-empty-booking-session-data";
import {BookingSessionStorage} from "../models/storage/booking-session-storage";
import {IDotRezInitialBookingData} from "./booking-initial-data.interface";
import {NullableString} from "../../../types/nullable-types";
import {IWizard} from "../../../models/wizard/wizard.interface";
import {IClientPaymentCallbacks} from "../../airline-webapi/requests/begin-payment-base.request";
import {IRoute} from "../../navigation/models/route.interface";
import {
    buildPaymentCanceledUrl,
    buildPaymentFailedUrl,
    buildPaymentSuccessUrl
} from "../models/payment/payment-status-query-params.enum";
import {BookingSessionStorageKeys} from "../models/storage/booking-session-storage-keys";
import {DotRezDomainCodes} from "../../dot-rez-api/data-contracts/enums/domain-codes.enum";
import {IStorage} from "../../storage/storage.interface";
import {UserSessionStorageKeys} from "../../storage/user-session-storage/user-session-storage-keys";


export interface IBookingStrategyOptions {
    bookingSession: IDotRezBookingSession;
    bookingSessionData: IDotRezBookingSessionData;
    searchController: IFlightSearchControllerViewModel;
    initialBookingData: IDotRezInitialBookingData | null;
}

const ActiveBookingStorageKeys = [
    UserSessionStorageKeys.ActiveBookingToken,
    UserSessionStorageKeys.ActiveBookingInitialData,
    UserSessionStorageKeys.ActiveBookingSearchControllerStorage
]


export abstract class BookingStrategyBase implements IBookingStrategy, IBookingStrategyToBookingAdapter, IAvailabilityFareReducer {

    constructor(public readonly services: IServiceFactory, options: IBookingStrategyOptions) {

        this._flightSearchController = options.searchController;
        if(options.initialBookingData) {
            this._initialBookingSnapshot = new BookingSnapshotModel(options.initialBookingData, services);
        } else {
            this._initialBookingSnapshot = new BookingSnapshotModel(createEmptyBookingSessionData(options.bookingSessionData.bookingData.currencyCode), services);
        }

        this._booking = new BookingModel(services, options.bookingSession, options.bookingSessionData, this);


        this._saveTokenInSessionStorage();
        this._persistInitialBookingDataInSessionStorage(options.initialBookingData);

        this._dangerousGoodsTermsAndConditionsAccepted = ('true' === this._booking.storage.getItem(BookingSessionStorageKeys.dangerousGoodsTermsAndConditionsAccepted));

        makeObservable<this, '_dangerousGoodsTermsAndConditionsAccepted'>(this, {
            steps: computed,
            _dangerousGoodsTermsAndConditionsAccepted: observable.ref,
        });
    }

    abstract get bookingFlowType(): BookingFlowTypeEnum;
    abstract get analyticsConfiguration(): IBookingStrategyAnalyticsConfiguration;
    protected abstract _createSteps(sessionStorage: IStorage<BookingSessionStorageKeys>): IWizard;
    abstract canShowSsr(ssrType: ISsrType): boolean;
    abstract get allowedShoppingCartModes(): ShoppingCartModeEnum[];
    abstract getPaymentRoute(): IRoute;
    abstract getFinalPriceRoute(): IRoute;
    abstract getFinalizePaymentRoute(): IRoute;
    abstract onPaymentSuccess(): Promise<void>;
    abstract get canBeModified(): boolean;

    get hideFinalPriceStep(): boolean {
        if(this.services.navigator.currentRoute.equals(this.getFinalPriceRoute())) {
            return false;
        }

        if(this.booking.hasPurchasesOnCurrentSession) {
            return this.booking.balanceDue.amount > 0 || this.booking.paymentHandler.totalAuxiliaryPaymentsAmount > 0;
        }

        return true;

    }

    protected _reactions: IReactionDisposer[] = [];


    get agentName(): NullableString {
        return this.flightSearchController.fields.agent.value || this.services.user.profile.userName;
    }

    get availabilityPricingInformationMessage(): string {
        return this.services.language.translate('All prices are per passenger and include airport taxes');
    }

    get allowHold(): boolean {
        return false;
    }

    createBookingStorage(token: string): BookingSessionStorage {
        return new BookingSessionStorage(token, this.services.userSessionStorage);
    }

    shouldFilterOutJourney(journey: JourneyModel): boolean {
        return false;
    }

    get activeCurrency(): string {
        if(this._booking) {
            return this._booking.currency;
        } else {
            return this.services.currency.current;
        }
   }

    get showStandardAndBlueBenefitsPrice(): boolean {
        return this.services.blueBenefits.isEnabled;
    }


    private _steps: IWizard | null = null;
    get steps(): IWizard {
        if(this._steps) {
            this._steps.dispose();
        }

        this._steps = this._createSteps(this.booking.storage);

        return this._steps;
    }

    private readonly _booking: BookingModel;
    get isBookingInitialized(): boolean {
        return Boolean(this._booking);
    }

    protected _persistInitialBookingDataInSessionStorage(initialBookingData: IDotRezInitialBookingData | null): void {
        if(initialBookingData && this.services.device.isWeb) {
            this.services.userSessionStorage.setJson(UserSessionStorageKeys.ActiveBookingInitialData, initialBookingData);
        } else {
            this.services.userSessionStorage.removeItem(UserSessionStorageKeys.ActiveBookingInitialData);
        }
    }


    protected _saveTokenInSessionStorage(): void {
        this.services.userSessionStorage.setItem(UserSessionStorageKeys.ActiveBookingToken, this.booking.session.token);
    }

    private readonly _flightSearchController: IFlightSearchControllerViewModel;
    get flightSearchController(): IFlightSearchControllerViewModel {
        return this._flightSearchController;
    }

    get booking(): BookingModel {
        return this._booking;
    }


    private _availability: AvailabilityModel | null = null;
    get availability(): AvailabilityModel {
        if(!this._availability) {
            this._availability = new AvailabilityModel(this.flightSearchController, this, this);

        }
        return this._availability;
    }

    createPrice(amount: number): Price {
        return this.booking.createPrice(amount);
    }

    private _cleanUpActiveBookingStorageKeys(): void {
        const sessionStorage = this.services.userSessionStorage;
        const activeToken = sessionStorage.getItem(UserSessionStorageKeys.ActiveBookingToken);

        if(activeToken === this.booking.token) {
            ActiveBookingStorageKeys.forEach(key => sessionStorage.removeItem(key));
        }
    }

    async dispose(): Promise<void> {
        this._reactions.forEach(r => r());
        this._reactions = [];
        this._steps?.dispose();

        this._availability?.dispose();
        this._lowFareReaderFactory?.dispose();
        this._flightSearchController?.dispose();

        this._cleanUpActiveBookingStorageKeys();
        if(this._booking) {
            try {
                await this._booking.reset();

            } catch(err){
                this.services.logger.warning('Failed to reset booking on dispose', err);
            } finally {
                this._booking.dispose();
            }
        }
    }

    get allowPromoCode(): boolean {
        return false;
    }

    async upgradeCurrentBookingSessionToAuthorizedUser(userName: string, password: string, domain: DotRezDomainCodes, previousUserNameHash: string): Promise<void> {
        await this._booking.upgradeToAuthorizedUser(userName, password, domain);

        const toUserNameHash = await this.services.crypto.computeHash(userName);

        ActiveBookingStorageKeys.forEach(key => {
            this.services.userSessionStorage.moveKey({
                fromUserNameHash: previousUserNameHash,
                toUserNameHash: toUserNameHash,
                key: key
            })
        })

        this.services.userSessionStorage.moveKey({
            fromUserNameHash: previousUserNameHash,
            toUserNameHash: toUserNameHash,
            key: UserSessionStorageKeys.BookingSessionStorage,
            storageOptions: {
                discriminator: this.booking.session.token
            }
        })

    }


    private readonly _initialBookingSnapshot: BookingSnapshotModel;
    get initialBookingSnapshot(): BookingSnapshotModel {
        return this._initialBookingSnapshot;
    }


    async sellDepartureJourney(fareToSell: IFareToSell, onAfterSell: (bookingSessionData: IDotRezPartialBookingSessionData) => Promise<void>): Promise<void> {
        throw new Error(`This booking strategy ${this.constructor.name} doesn't support sellDepartureJourney`);
    }

    async sellDepartureJourneyBundle(bundleCode: string): Promise<void> {
        throw new Error(`This booking strategy ${this.constructor.name} doesn't support sellDepartureJourneyBundle`);
    }

    async sellReturnJourney(fareToSell: IFareToSell, onAfterSell: (bookingSessionData: IDotRezPartialBookingSessionData) => Promise<void>): Promise<void> {
        throw new Error(`This booking strategy ${this.constructor.name} doesn't support sellReturnJourney`);
    }

    async sellReturnJourneyBundle(bundleCode: string): Promise<void> {
        throw new Error(`This booking strategy ${this.constructor.name} doesn't support sellReturnJourneyBundle`);
    }

    private _lowFareReaderFactory: RoundTripLowFareReaderFactory | null = null;
    getLowFaresReader(): IRoundTripLowFareReader {
        if(!this._lowFareReaderFactory) {
            this._lowFareReaderFactory = new RoundTripLowFareReaderFactory(this.services,
                this.flightSearchController,
                true,
                () => this.activeCurrency);
        }
        return this._lowFareReaderFactory.lowFareReader;

    }

    reduceDepartureFare(price: Price): Price {
        return price;
    }

    reduceReturnFare(price: Price): Price {
        return price;
    }

    get departureBundleNameToShowOnFare(): NullableString {
        if(this.isBookingInitialized) {
            return this.booking.departureJourney?.selectedBundle?.bundleType.bundleName || null;
        }

        return null;
    }

    get returnBundleNameToShowOnFare(): NullableString {

        if(this.isBookingInitialized) {
            return this.booking.returnJourney?.selectedBundle?.bundleType.bundleName || null;
        }

        return null;
    }

    get allowSellInsurance(): boolean {
        return false;
    }

    get shouldSyncContactWithPrimaryPassenger(): boolean {
        return false;
    }

    get supportsPayment(): boolean {
        return true;
    }

    getPaymentCallbacks(): IClientPaymentCallbacks {
        const finalizePaymentRoute = this.getFinalizePaymentRoute();
        return {
            success: buildPaymentSuccessUrl(finalizePaymentRoute.path),
            cancel: buildPaymentCanceledUrl(finalizePaymentRoute.path),
            failed: buildPaymentFailedUrl(finalizePaymentRoute.path)
        }
    }

    async goBackToFlightSelection(): Promise<void> {

    }

    private _dangerousGoodsTermsAndConditionsAccepted = false;
    get dangerousGoodsTermsAndConditionsAccepted(): boolean {
        return this._dangerousGoodsTermsAndConditionsAccepted;
    }

    set dangerousGoodsTermsAndConditionsAccepted(value: boolean) {
        runInAction(() => {
            this._dangerousGoodsTermsAndConditionsAccepted = value;
            this.booking.storage.setItem(BookingSessionStorageKeys.dangerousGoodsTermsAndConditionsAccepted, value.toString());
        })
    }

    get allowChangeSearch(): boolean {
        return false;
    }

    get shouldRenderBagsInExtras(): boolean {
        return true;
    }

    get shouldValidateBags(): boolean {
        return false;
    }
}
